/**
 * Email + password authentication.
 *
 * Mirrors the shape of the sibling providers (chromeIdentityAuth, appleIdentityAuth,
 * nativeAppAuth): every credential call is preceded by setPersistence, and the
 * result is the same `{ user, error? }` envelope.
 *
 * Two things make this file different from the others:
 *
 * 1. Imports come from `firebase/auth/web-extension`, NOT `firebase/auth`. That
 *    entry point is the deliberate MV3 fix (no remotely-hosted gapi/reCAPTCHA
 *    code) and it is what `config/firebaseConfig` builds `auth` from. A plain
 *    `firebase/auth` import would bundle a second auth instance. Unlike
 *    signInWithPopup, every function used here is a plain REST call to
 *    identitytoolkit, so no dynamic import is needed.
 *
 * 2. An unverified password account is NEVER returned as a signed-in user. The
 *    backend rejects its token (see backend-graphql/src/authPolicy.js), so
 *    promoting it here would put the app in a state where every request 401s.
 */
import {
    createUserWithEmailAndPassword,
    indexedDBLocalPersistence,
    reload,
    setPersistence,
    signInWithEmailAndPassword,
    updateProfile,
} from 'firebase/auth/web-extension';
import type { User } from 'firebase/auth';
import { auth } from '../config/firebaseConfig';
import type { AuthUser } from './bgAuthBridge';
import { log, error as logError } from '../utils/logger';
import { isWeakPattern } from './commonPasswords';
import { detectLocale } from '../i18n/detect';

export interface EmailAuthResult {
    user: AuthUser | null;
    /** Set when the account exists but has not verified its email address. */
    pendingVerification?: { email: string };
    error?: string;
    /** Raw Firebase error code (e.g. 'auth/invalid-credential') for i18n mapping. */
    errorCode?: string;
}

/**
 * NIST-aligned: length is what matters, composition rules are not. 12 rather than
 * Firebase's own floor of 6.
 *
 * This is enforced in our client only — standard Firebase Auth cannot enforce a
 * policy server-side (that is an Identity Platform feature we deliberately did
 * not buy). It protects users from weak choices; it does not stop an attacker
 * calling identitytoolkit directly.
 */
export const PASSWORD_MIN_LENGTH = 12;

export type PasswordProblem = 'tooShort' | 'common' | null;

/** Pure and offline — no network call, no SDK call. */
export function validatePasswordLocally(password: string): PasswordProblem {
    if (!password || password.length < PASSWORD_MIN_LENGTH) return 'tooShort';
    if (isWeakPattern(password.toLowerCase())) return 'common';
    return null;
}

const toAuthUser = (user: User): AuthUser => ({
    uid: user.uid,
    displayName: user.displayName || null,
    email: user.email || null,
    photoURL: user.photoURL || null,
    providerId: user.providerData[0]?.providerId || 'password',
});

const isPasswordAccount = (user: User): boolean =>
    user.providerData.some((entry: { providerId: string }) => entry.providerId === 'password');

const failure = (err: unknown, context: string): EmailAuthResult => {
    const code = (err as { code?: string })?.code;
    // NOTE: never log the password. logger is dev-gated, but the rule holds anyway.
    logError(`[EmailAuth] ${context} failed:`, code || err);
    return {
        user: null,
        error: err instanceof Error ? err.message : String(err),
        errorCode: code,
    };
};

/**
 * Ask OUR backend to send the verification / reset email.
 *
 * Deliberately not the SDK's sendEmailVerification / sendPasswordResetEmail:
 * those are delivered by Firebase from noreply@<project>.firebaseapp.com, which
 * is the first thing a recipient sees in their inbox and reads like phishing for
 * an app they just signed up to. The backend mints the same action link with the
 * Admin SDK and delivers it through Resend from hello@rhemn.com, branded and in
 * the user's language.
 *
 * The backend always reports success — a throttled send, an unknown address and
 * a real send are indistinguishable — so this resolves true unless the network
 * itself failed.
 */
async function requestAuthEmail(email: string, kind: 'verify' | 'reset'): Promise<boolean> {
    const url = process.env.REACT_APP_GRAPHQL_API_URL;
    if (!url) {
        logError('[EmailAuth] REACT_APP_GRAPHQL_API_URL unset — cannot send auth email');
        return false;
    }
    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                query: 'mutation SendAuthEmail($email: String!, $kind: AuthEmailKind!, $locale: String) { sendAuthEmail(email: $email, kind: $kind, locale: $locale) }',
                variables: { email: email.trim(), kind, locale: detectLocale() },
            }),
        });
        if (!res.ok) return false;
        const json = await res.json();
        return json?.data?.sendAuthEmail === true;
    } catch (err) {
        logError('[EmailAuth] auth email request failed:', err);
        return false;
    }
}

/**
 * Create an account. Always returns `pendingVerification` — never a signed-in
 * user — because the founder's decision is a hard gate: no app access until the
 * verification link is clicked.
 */
export async function signUpWithEmail(
    email: string,
    password: string,
    displayName?: string,
): Promise<EmailAuthResult> {
    try {
        await setPersistence(auth, indexedDBLocalPersistence);
        const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);

        if (displayName?.trim()) {
            await updateProfile(cred.user, { displayName: displayName.trim() });
        }

        await requestAuthEmail(cred.user.email || email.trim(), 'verify');
        log('[EmailAuth] Account created, verification sent');

        return { user: null, pendingVerification: { email: cred.user.email || email.trim() } };
    } catch (err) {
        return failure(err, 'sign-up');
    }
}

/**
 * Sign in. An unverified password account comes back as `pendingVerification`
 * rather than a user — the Firebase session exists at that point, which is what
 * lets the verify step poll and resend.
 */
export async function signInWithEmail(email: string, password: string): Promise<EmailAuthResult> {
    try {
        await setPersistence(auth, indexedDBLocalPersistence);
        const cred = await signInWithEmailAndPassword(auth, email.trim(), password);

        if (isPasswordAccount(cred.user) && !cred.user.emailVerified) {
            log('[EmailAuth] Signed in but email not verified');
            return { user: null, pendingVerification: { email: cred.user.email || email.trim() } };
        }

        return { user: toAuthUser(cred.user) };
    } catch (err) {
        return failure(err, 'sign-in');
    }
}

export async function resendVerification(): Promise<{ ok: boolean; error?: string; errorCode?: string }> {
    try {
        const user = auth.currentUser;
        if (!user) return { ok: false, error: 'No active session' };
        const ok = await requestAuthEmail(user.email || '', 'verify');
        return ok ? { ok: true } : { ok: false, errorCode: 'auth/network-request-failed' };
    } catch (err) {
        const { error, errorCode } = failure(err, 'resend-verification');
        return { ok: false, error, errorCode };
    }
}

/**
 * Re-check whether the pending account has verified.
 *
 * ── THE TRAP ─────────────────────────────────────────────────────────────────
 * `reload()` refreshes the local User object's `emailVerified` flag via
 * getAccountInfo. It does NOT refresh the cached ID token — and `email_verified`
 * is a claim baked into the JWT. graphqlClient.getIdToken() only auto-refreshes
 * within 5 minutes of expiry, so without the forced refresh below the app would
 * keep sending a token carrying `email_verified: false` for up to another ~55
 * minutes, and the backend gate would reject every single request.
 *
 * The `getIdToken(true)` is therefore load-bearing. Do not remove it.
 */
export async function refreshVerificationStatus(): Promise<EmailAuthResult> {
    try {
        const user = auth.currentUser;
        if (!user) return { user: null };

        await reload(user);
        if (!user.emailVerified) {
            return { user: null, pendingVerification: { email: user.email || '' } };
        }

        await user.getIdToken(true);
        log('[EmailAuth] Email verified, token refreshed');

        return { user: toAuthUser(user) };
    } catch (err) {
        return failure(err, 'refresh-verification');
    }
}

/**
 * Send a password reset link.
 *
 * Always reports success. With email-enumeration protection on, Firebase itself
 * resolves silently for an unknown address; surfacing auth/user-not-found would
 * reintroduce exactly the oracle that protection exists to close.
 */
export async function sendReset(email: string): Promise<{ ok: boolean; error?: string; errorCode?: string }> {
    const ok = await requestAuthEmail(email, 'reset');
    // The backend cannot tell us whether the address exists, by design. Only a
    // transport failure is reportable; anything else shows the same "check your
    // inbox" screen.
    return ok ? { ok: true } : { ok: false, errorCode: 'auth/network-request-failed' };
}

/**
 * Ask the backend which federated provider owns an email, so the UI can say
 * "this email is already set up with Google" instead of dead-ending the user.
 *
 * Uses a plain fetch rather than graphqlClient.graphqlRequest: this runs BEFORE
 * any session exists, and graphqlRequest resolves an ID token first (waiting 5s
 * on onAuthStateChanged before rejecting).
 *
 * Fails open in every direction — a null result simply means "proceed to the
 * password step". The backend returns null for a password account AND for an
 * unknown email, indistinguishably, so this can never be used to probe whether
 * someone has an account.
 */
export async function fetchProviderHint(email: string, timeoutMs = 3500): Promise<string | null> {
    const url = process.env.REACT_APP_GRAPHQL_API_URL;
    if (!url) return null;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                query: 'query ProviderHint($email: String!) { federatedProviderHint(email: $email) }',
                variables: { email: email.trim() },
            }),
            signal: controller.signal,
        });
        if (!res.ok) return null;
        const json = await res.json();
        return json?.data?.federatedProviderHint ?? null;
    } catch {
        return null;
    } finally {
        clearTimeout(timer);
    }
}
